import { useState } from 'react';
import { Num, Plural, T, Var } from 'gt-react';
import { FilePlusIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Spinner } from '@/components/ui/spinner';
import { formatFileSize } from '@/lib/pricelist-utils';
import {
  useEmailArchiveMembers,
  useExtractEmailArchiveMembers,
} from '@/api/rpc/hooks/ship-and-debit/useLabeling';
import {
  SD_849_ARCHIVE_MAX_EXTRACT_FILES,
  type EmailArchiveMember,
} from '@erp/rpc';
import { AttachmentFileIcon } from './AttachmentFileIcon';
import {
  isPickable,
  pickBlocker,
  pickablePaths,
  selectAllState,
  togglePick,
} from './archivePicker';

/**
 * A zip attached to the email, opened as the list of files inside it.
 *
 * The reviewer ticks the files that answer the claim and adds them to the
 * email; each one then shows up under "Which part holds the accept/reject
 * lines?" with its own role, and the parse reads it like any attachment. The
 * zip itself is never read as a report — nothing reads inside one.
 */
export function ArchiveMemberPicker({
  inboundEmailId,
  attachmentId,
}: {
  inboundEmailId: string;
  attachmentId: string;
}) {
  const listing = useEmailArchiveMembers({ inboundEmailId, attachmentId });
  const extract = useExtractEmailArchiveMembers();
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [addedCount, setAddedCount] = useState<number | null>(null);

  if (listing.isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }
  if (!listing.data) {
    return (
      <p className="px-3 py-4 text-sm text-muted-foreground">
        <T context="849 review zip picker: listing failed">
          The files in this zip could not be read.
        </T>
      </p>
    );
  }

  const { members, truncated } = listing.data;
  const blocker = pickBlocker(selected);
  const allState = selectAllState(members, selected);

  function add() {
    if (blocker) return;
    extract.mutate(
      { inboundEmailId, attachmentId, paths: [...selected] },
      {
        onSuccess: (result) => {
          setSelected(new Set());
          setAddedCount(result.attachments.length);
        },
      },
    );
  }

  return (
    <div className="space-y-3 p-3">
      <p className="text-sm text-muted-foreground">
        <T context="849 review zip picker: instructions">
          Tick the files that answer the claim and add them to the email. Each
          one then gets its own answer under “Which part holds the accept/reject
          lines?”.
        </T>
      </p>

      {members.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          <T context="849 review zip picker: empty zip">
            This zip holds no files.
          </T>
        </p>
      ) : (
        <div className="divide-y rounded border">
          <label className="flex cursor-pointer items-center gap-2 bg-muted/40 px-3 py-2 text-sm font-medium">
            <Checkbox
              checked={allState}
              disabled={
                pickablePaths(members).length === 0 || extract.isPending
              }
              onCheckedChange={(checked) =>
                setSelected(
                  checked === true
                    ? new Set(pickablePaths(members))
                    : new Set(),
                )
              }
            />
            <T context="849 review zip picker: select all files">
              All files (<Var>{members.length}</Var>)
            </T>
          </label>
          {members.map((member) => (
            <MemberRow
              key={member.path}
              member={member}
              picked={selected.has(member.path)}
              disabled={extract.isPending}
              onPick={(picked) =>
                setSelected((prev) => togglePick(prev, member.path, picked))
              }
            />
          ))}
        </div>
      )}

      {truncated ? (
        <p className="text-xs text-muted-foreground">
          <T context="849 review zip picker: more files than listed">
            The zip holds more files than can be listed here; only the first
            ones are shown.
          </T>
        </p>
      ) : null}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          onClick={add}
          disabled={blocker != null || extract.isPending}
        >
          {extract.isPending ? (
            <Spinner className="h-4 w-4" />
          ) : (
            <FilePlusIcon className="h-4 w-4" />
          )}
          <T context="849 review zip picker: add picked files button">
            Add <Num>{selected.size}</Num>{' '}
            <Plural n={selected.size} one="File" other="Files" /> to Email
          </T>
        </Button>
        {blocker === 'too_many' ? (
          <span className="text-xs text-amber-600 dark:text-amber-500">
            <T context="849 review zip picker: too many files picked">
              Add at most <Var>{SD_849_ARCHIVE_MAX_EXTRACT_FILES}</Var> files at
              a time.
            </T>
          </span>
        ) : null}
        {addedCount != null && selected.size === 0 ? (
          <span className="text-xs text-muted-foreground">
            <T context="849 review zip picker: files added confirmation">
              <Plural
                n={addedCount}
                one={
                  <>
                    1 file is now on the email. Mark it “Has the decision” if it
                    carries the decision.
                  </>
                }
                other={
                  <>
                    <Num>{addedCount}</Num> files are now on the email. Mark the
                    ones with the decision as “Has the decision”.
                  </>
                }
              />
            </T>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function MemberRow({
  member,
  picked,
  disabled,
  onPick,
}: {
  member: EmailArchiveMember;
  picked: boolean;
  disabled: boolean;
  onPick: (picked: boolean) => void;
}) {
  const added = member.attachmentId != null;
  return (
    <label className="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm">
      <Checkbox
        checked={added || picked}
        disabled={disabled || !isPickable(member)}
        onCheckedChange={(checked) => onPick(checked === true)}
      />
      <AttachmentFileIcon mimeType="" fileName={member.fileName} />
      <span className="min-w-0 flex-1 truncate" title={member.path}>
        {member.fileName}
      </span>
      <span className="shrink-0 text-xs text-muted-foreground">
        {formatFileSize(member.sizeBytes)}
      </span>
      <MemberStatus member={member} />
    </label>
  );
}

function MemberStatus({ member }: { member: EmailArchiveMember }) {
  if (member.attachmentId != null) {
    return (
      <Badge variant="secondary" className="shrink-0">
        <T context="849 review zip picker: file already on the email">
          On the email
        </T>
      </Badge>
    );
  }
  if (member.unavailableReason === 'encrypted') {
    return (
      <Badge variant="outline" className="shrink-0">
        <T context="849 review zip picker: password-protected file">
          Password-protected
        </T>
      </Badge>
    );
  }
  if (member.unavailableReason === 'too_large') {
    return (
      <Badge variant="outline" className="shrink-0">
        <T context="849 review zip picker: file too large to add">Too large</T>
      </Badge>
    );
  }
  return null;
}
